import React from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import MFASetup from "@/components/MFASetup";
import SessionsPanel from "@/components/SessionsPanel";
import PreferencesPanel from "@/components/PreferencesPanel";
import { Server, Shield, User as UserIcon, Palette, KeyRound, Smartphone, Bell } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Workspace preferences, security posture, and platform configuration." eyebrow="Preferences" />
      <div className="p-6 lg:p-10 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl">
        {[
          { icon: UserIcon, title: "Account", tone: "emerald", content: (
            <div className="text-sm space-y-1.5">
              <Row k="Username" v={user?.username} />
              <Row k="Email" v={user?.email} />
              <Row k="Role" v={user?.role} />
            </div>
          )},
          { icon: Bell, title: "Notifications & timezone", tone: "sun", content: <PreferencesPanel /> },
          { icon: Palette, title: "Appearance", tone: "sun", content: (
            <div className="text-sm text-muted-foreground">
              Light theme with green/red/yellow accent palette, 3D animated highlights.
            </div>
          )},
          { icon: Server, title: "Default mail servers", tone: "emerald", content: (
            <div className="text-sm space-y-1 font-mono">
              <div><span className="text-muted-foreground">IMAP:</span> imap.hostinger.com : 993 (SSL/TLS)</div>
              <div><span className="text-muted-foreground">SMTP:</span> smtp.hostinger.com : 465 (SSL/TLS)</div>
              <div className="text-xs text-muted-foreground pt-2 font-sans">Editable per email account.</div>
            </div>
          )},
          { icon: Shield, title: "Security", tone: "crimson", content: (
            <ul className="text-xs text-muted-foreground space-y-1.5">
              <li>· Credentials Fernet-encrypted at rest</li>
              <li>· JWT bearer sessions</li>
              <li>· Company-scoped access enforced server side</li>
              <li>· All actions written to audit log</li>
              <li>· Attachments limited to 15MB, 5 per email</li>
            </ul>
          )},
          { icon: KeyRound, title: "Two-factor authentication", tone: "emerald", content: <MFASetup /> },
          { icon: Smartphone, title: "Active sessions", tone: "sun", content: <SessionsPanel /> },
        ].map((c, i) => {
          const tones = { emerald: "bg-emerald-brand/12 text-emerald-brand", sun: "bg-sun/25 text-yellow-700", crimson: "bg-crimson/12 text-crimson" };
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.06 }} className="card-3d p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`h-10 w-10 rounded-xl grid place-items-center ${tones[c.tone]}`}><c.icon className="h-5 w-5" /></div>
                <div className="font-display text-lg font-semibold tracking-tight">{c.title}</div>
              </div>
              {c.content}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between py-1 border-b border-border last:border-b-0">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-mono">{v}</span>
    </div>
  );
}
